import React, { useMemo } from 'react';
import { User, CheckIn } from '../types';
import { isTodayInTurkey, formatTurkeyDate } from '../utils';

interface AdminCheckInStatusProps {
  users: User[];
  checkIns: CheckIn[];
}

const AdminCheckInStatus: React.FC<AdminCheckInStatusProps> = ({ users, checkIns }) => {
  const { checkedInUsers, notCheckedInUsers, todayCheckIns } = useMemo(() => {
    const todayCheckIns = checkIns.filter(checkIn => 
      isTodayInTurkey(checkIn.timestamp)
    );

    const checkedInUserIds = new Set(todayCheckIns.map(checkIn => checkIn.userId));
    
    // Filter out admin users and disabled users from all calculations
    const activeUsers = users.filter(user => 
      user.email !== 'admin@mecon.com' && 
      user.email !== 'muhasebe@meconyapi.com' &&
      !user.disabled
    );
    
    const checkedInUsers = activeUsers.filter(user => checkedInUserIds.has(user.id));
    const notCheckedInUsers = activeUsers.filter(user => !checkedInUserIds.has(user.id));

    return { checkedInUsers, notCheckedInUsers, todayCheckIns };
  }, [users, checkIns]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-2">Bugünkü Giriş Durumu</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-medium">✓</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-green-800">Giriş Yapanlar</p>
                <p className="text-2xl font-bold text-green-900">{checkedInUsers.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-medium">✗</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-red-800">Giriş Yapmayanlar</p>
                <p className="text-2xl font-bold text-red-900">{notCheckedInUsers.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-medium">👥</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-blue-800">Toplam Kullanıcı</p>
                <p className="text-2xl font-bold text-blue-900">{checkedInUsers.length + notCheckedInUsers.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Checked In Users */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            Giriş Yapan Kullanıcılar ({checkedInUsers.length})
          </h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {checkedInUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                Henüz giriş yapan kullanıcı yok
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {checkedInUsers.map((user) => {
                  const userCheckIn = todayCheckIns.find(checkIn => checkIn.userId === user.id);
                  return (
                    <li key={user.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          {user.workLocation.address && (
                            <p className="text-xs text-blue-600 mt-1">
                              İş Yeri: {user.workLocation.name || user.workLocation.address}
                            </p>
                          )}
                          {userCheckIn && (
                            <div className="mt-1 space-y-1">
                              <p className="text-xs text-gray-500">
                                Konum: {userCheckIn.location.address || `${userCheckIn.location.latitude.toFixed(6)}, ${userCheckIn.location.longitude.toFixed(6)}`}
                              </p>
                              <p className="text-xs text-gray-600">
                                Mesafe: {userCheckIn.distance.toFixed(2)} km
                              </p>
                              <p className="text-xs text-green-600">
                                Giriş: {formatTurkeyDate(userCheckIn.timestamp)}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Giriş Yaptı
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Not Checked In Users */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
            <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
            Giriş Yapmayan Kullanıcılar ({notCheckedInUsers.length})
          </h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {notCheckedInUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                Tüm kullanıcılar giriş yaptı! 🎉
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {notCheckedInUsers.map((user) => (
                  <li key={user.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        {user.workLocation.address && (
                          <p className="text-xs text-blue-600 mt-1">
                            İş Yeri: {user.workLocation.name || user.workLocation.address}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Giriş Yapmadı
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCheckInStatus; 